/**
 * PC-v2.2-A — INDICADORES DEL PORTAL DE UN CLIENTE (administración, `portal:configurar`: sólo ADMIN)
 *
 * Qué indicadores enseña el portal a este cliente, en todos sus centros o en uno. Cada indicador opcional se puede
 * mostrar, ocultar o dejar en «Heredar» (lo que diga el nivel de arriba: el cliente para un centro, el catálogo
 * general para el cliente). Los esenciales se ven siempre. No es un diseñador de paneles: no cambia ninguna cifra
 * ni da acceso a ningún dato; sólo decide qué se enseña. El servidor valida todo y rechaza con 409 si otra persona
 * cambió este mismo nivel mientras se editaba.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  api,
  type Centro,
  type ClienteDetalle,
  type ConfiguracionIndicadoresPortal,
  type IndicadorPortalConfigurado,
  type ModuloIndicadorPortal,
  FalloApi,
} from '../api.ts';
import { Error as ErrorCaja, Panel, useDatos, Vacio } from '../componentes/base.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useSesion } from '../contexto.tsx';

const MODULOS: readonly { clave: ModuloIndicadorPortal; texto: string }[] = [
  { clave: 'INICIO', texto: 'Inicio' },
  { clave: 'CALENDARIO', texto: 'Calendario de limpieza' },
  { clave: 'CALIDAD', texto: 'Calidad' },
];

type Valor = boolean | null;
const aOpcion = (v: Valor): string => (v === null ? 'heredar' : v ? 'mostrar' : 'ocultar');
const deOpcion = (o: string): Valor => (o === 'heredar' ? null : o === 'mostrar');
const textoVisible = (v: boolean): string => (v ? 'Visible' : 'Oculto');

export function PaginaIndicadoresPortal(): ReactNode {
  const { idCliente = '' } = useParams();
  const { puede } = useSesion();
  const cliente = useDatos(() => api.get<ClienteDetalle>(`/api/clientes/${idCliente}/detalle`), [idCliente]);
  const centros = useDatos(() => api.get<{ centros: Centro[] }>(`/api/clientes/${idCliente}/centros`), [idCliente]);
  const [idCentro, setIdCentro] = useState('');
  //  Aquí y no en el editor: tras guardar (o un 409) la configuración se recarga y el editor se monta de nuevo.
  const [aviso, setAviso] = useState<{ ok: boolean; texto: string } | null>(null);
  useEffect(() => {
    if (aviso === null || !aviso.ok) return undefined;
    const t = setTimeout(() => setAviso(null), 6000);
    return () => clearTimeout(t);
  }, [aviso]);
  const ruta = `/api/clientes/${idCliente}/portal/indicadores${idCentro === '' ? '' : `?idCentro=${idCentro}`}`;
  const config = useDatos(() => api.get<ConfiguracionIndicadoresPortal>(ruta), [ruta]);
  const migas = [
    { texto: 'Clientes', a: '/clientes' },
    { texto: cliente.datos?.razonSocial ?? '…', a: `/clientes/${idCliente}` },
    { texto: 'Indicadores del portal' },
  ];

  if (!puede('portal:configurar')) {
    return <Shell migas={migas} titulo="Indicadores del portal"><div className="panel"><Vacio titulo="Tu perfil no puede configurar el portal del cliente" /></div></Shell>;
  }
  return (
    <Shell
      migas={migas}
      titulo="Indicadores del portal"
      subtitulo="Qué cifras ve este cliente en su portal. Mostrar u ocultar no cambia ningún dato ni da acceso a nada más."
    >
      <div className="panel">
        <div className="panel-cuerpo">
          <div className="campo" style={{ maxWidth: 420, marginBottom: 0 }}>
            <label htmlFor="nivel-indicadores">Aplicar a</label>
            <select id="nivel-indicadores" value={idCentro} onChange={(e) => { setIdCentro(e.target.value); setAviso(null); }}>
              <option value="">Todos los centros del cliente</option>
              {(centros.datos?.centros ?? []).map((c) => (
                <option key={c.id} value={c.id}>Sólo el centro {c.descripcion}{c.activo ? '' : ' (inactivo)'}</option>
              ))}
            </select>
          </div>
          <p className="pequena suave" style={{ marginBottom: 0 }}>
            {idCentro === ''
              ? 'Lo que elijas aquí vale para todos los centros, salvo los que tengan su propia excepción.'
              : 'Una excepción de este centro sólo le afecta a él. «Heredar» usa lo que esté elegido para todo el cliente.'}
          </p>
          <p className="nota pequena" style={{ marginBottom: 0 }}>
            El portal lo aplica en Inicio, Calidad y el Calendario de limpieza. Ocultar un indicador sólo lo quita de la
            pantalla: no cambia ninguna cifra ni lo que el cliente puede consultar.
          </p>
        </div>
      </div>
      {aviso !== null && <div className={`nota ${aviso.ok ? 'exito' : 'error'}`} role={aviso.ok ? 'status' : 'alert'}>{aviso.texto}</div>}
      <Panel estado={config}>
        {(d) => (
          <EditorNivel key={`${d.idCentro ?? ''}:${d.version}`} config={d} ruta={ruta}
            alTerminar={(a) => { setAviso(a); config.recargar(); }} />
        )}
      </Panel>
      <p className="pequena suave">
        <Link to={`/clientes/${idCliente}`}>Volver al cliente</Link>
      </p>
    </Shell>
  );
}

function EditorNivel({ config, ruta, alTerminar }: {
  config: ConfiguracionIndicadoresPortal; ruta: string; alTerminar: (aviso: { ok: boolean; texto: string }) => void;
}): ReactNode {
  const [borrador, setBorrador] = useState<ReadonlyMap<string, Valor>>(() => new Map(config.indicadores.map((i) => [i.id, i.propio])));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const cambios = useMemo(
    () => config.indicadores.filter((i) => !i.esencial && borrador.get(i.id) !== i.propio).map((i) => ({ id: i.id, visible: borrador.get(i.id) ?? null })),
    [config, borrador],
  );
  const conExcepcion = config.indicadores.filter((i) => !i.esencial && (borrador.get(i.id) ?? null) !== null).length;
  const quien = config.nivel === 'CLIENTE' ? 'del catálogo general' : 'de todo el cliente';

  async function guardar(): Promise<void> {
    setGuardando(true);
    setError(null);
    try {
      await api.put(ruta, { version: config.version, cambios });
      alTerminar({ ok: true, texto: `Configuración guardada: ${String(cambios.length)} cambio(s).` });
    } catch (e) {
      setGuardando(false);
      if (e instanceof FalloApi && e.codigo === 'CONFIG_MODIFICADA') {
        alTerminar({ ok: false, texto: 'Otra persona ha cambiado esta configuración mientras la editabas. Se han cargado los valores actuales: revísalos y vuelve a guardar.' });
      } else {
        setError(e);
      }
    }
  }

  //  PC-v2.2-C1: el resultado de un gráfico depende también de las cifras que enseña (con lo que hay sin guardar).
  const propioDe = new Map(config.indicadores.map((i) => [i.id, i.esencial ? true : borrador.get(i.id) ?? i.heredado.visible]));
  const nombreDe = new Map(config.indicadores.map((i) => [i.id, i.nombre]));
  const ocultoPor = (i: IndicadorPortalConfigurado): string[] =>
    (i.dependeDe ?? []).filter((d) => propioDe.get(d) !== true).map((d) => nombreDe.get(d) ?? d);

  return (
    <>
      {error !== null && <ErrorCaja error={error} />}
      {MODULOS.map((m) => {
        const del = config.indicadores.filter((i) => i.modulo === m.clave);
        if (del.length === 0) return null;
        return (
          <section key={m.clave} className="panel" aria-labelledby={`mod-${m.clave}`}>
            <div className="panel-cabecera"><h2 id={`mod-${m.clave}`}>{m.texto}</h2></div>
            <div className="tabla-desliza">
              <table className="tabla-tarjetas" aria-label={`Indicadores de ${m.texto}`}>
                <thead><tr><th>Indicador</th><th>Bloque</th><th>En este nivel</th><th>Resultado</th></tr></thead>
                <tbody>
                  {del.map((i) => (
                    <Fila key={i.id} i={i} valor={borrador.get(i.id) ?? null} quien={quien} deshabilitado={guardando} ocultoPor={ocultoPor(i)}
                      alCambiar={(v) => setBorrador((b) => new Map(b).set(i.id, v))} />
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
      <div className="fila" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <button type="button" className="principal" disabled={guardando || cambios.length === 0} onClick={() => void guardar()}>
          {guardando ? 'Guardando…' : cambios.length === 0 ? 'Sin cambios' : `Guardar ${String(cambios.length)} cambio(s)`}
        </button>
        <button type="button" disabled={guardando || cambios.length === 0}
          onClick={() => setBorrador(new Map(config.indicadores.map((i) => [i.id, i.propio])))}>
          Descartar cambios
        </button>
        <button type="button" disabled={guardando || conExcepcion === 0}
          onClick={() => setBorrador(new Map(config.indicadores.map((i) => [i.id, null])))}>
          Heredar todo en este nivel
        </button>
      </div>
    </>
  );
}

function Fila({ i, valor, quien, deshabilitado, ocultoPor, alCambiar }: {
  i: IndicadorPortalConfigurado; valor: Valor; quien: string; deshabilitado: boolean; ocultoPor: readonly string[]; alCambiar: (v: Valor) => void;
}): ReactNode {
  const propio = i.esencial ? true : valor ?? i.heredado.visible;
  const resultado = propio && ocultoPor.length === 0;
  const idSelect = `ind-${i.id.replaceAll('.', '-')}`;
  return (
    <tr data-indicador={i.id}>
      <td className="celda-titulo">
        <label htmlFor={idSelect} style={{ fontWeight: 600 }}>{i.nombre}</label>
        <div className="pequena suave">{i.descripcion}</div>
        {(i.dependeDe ?? []).length > 0 && <div className="pequena tenue">Gráfico: se ve sólo si también se ven las cifras que enseña.</div>}
      </td>
      <td data-label="Bloque">{i.grupo}</td>
      <td data-label="En este nivel">
        {i.esencial ? (
          <span className="etiqueta neutra" title="Imprescindible para conocer el estado del servicio">Esencial · siempre visible</span>
        ) : (
          <select id={idSelect} value={aOpcion(valor)} disabled={deshabilitado} onChange={(e) => alCambiar(deOpcion(e.target.value))}>
            <option value="heredar">Heredar ({textoVisible(i.heredado.visible).toLowerCase()} {quien})</option>
            <option value="mostrar">Mostrar</option>
            <option value="ocultar">Ocultar</option>
          </select>
        )}
      </td>
      <td data-label="Resultado">
        <span className={`etiqueta ${resultado ? 'exito' : 'neutra'}`}>{textoVisible(resultado)}</span>
        {propio && ocultoPor.length > 0 && <div className="pequena aviso">Oculto porque no se ve: {ocultoPor.join(', ')}.</div>}
        {!i.esencial && valor !== i.propio && <span className="pequena aviso" style={{ marginLeft: 6 }}>sin guardar</span>}
      </td>
    </tr>
  );
}
